<template>
  <div class="merchant-detail" v-loading="loading">
    <el-card shadow="hover" v-if="merchant.id">
      <!-- 头部信息 -->
      <div class="detail-header">
        <el-image
          :src="merchant.logo"
          style="width: 80px; height: 80px; border-radius: 8px;"
          fit="cover"
        >
          <div slot="error" class="image-slot">
            <i class="el-icon-picture-outline" style="font-size: 30px; color: #c0c4cc;"></i>
          </div>
        </el-image>
        <div class="header-info">
          <h2>{{ merchant.name }} <small v-if="merchant.storeName">({{ merchant.storeName }})</small></h2>
          <div class="tags">
            <el-tag :type="merchant.joinStatus === 1 ? 'success' : merchant.joinStatus === 0 ? 'warning' : 'danger'" size="small">
              {{ statusText(merchant.joinStatus) }}
            </el-tag>
            <el-tag :type="merchant.status === 1 ? 'success' : 'info'" size="small">
              {{ merchant.status === 1 ? '营业中' : '已关闭' }}
            </el-tag>
          </div>
        </div>
        <div class="header-actions">
          <el-button @click="$router.back()">返回</el-button>
          <template v-if="merchant.joinStatus === 0">
            <el-button type="success" @click="handleApprove">通过</el-button>
            <el-button type="danger" @click="handleReject">驳回</el-button>
          </template>
        </div>
      </div>

      <el-divider></el-divider>

      <!-- 详细信息 -->
      <el-row :gutter="24" class="detail-body">
        <el-col :span="12">
          <h4>基本信息</h4>
          <el-descriptions :column="1" border size="medium">
            <el-descriptions-item label="商家名称">{{ merchant.name }}</el-descriptions-item>
            <el-descriptions-item label="门店名称">{{ merchant.storeName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="联系人">{{ merchant.contact || '-' }}</el-descriptions-item>
            <el-descriptions-item label="联系电话">{{ merchant.phone || '-' }}</el-descriptions-item>
            <el-descriptions-item label="微信">{{ merchant.wechat || '-' }}</el-descriptions-item>
            <el-descriptions-item label="地址">{{ merchant.address || '-' }}</el-descriptions-item>
          </el-descriptions>
        </el-col>
        <el-col :span="12">
          <h4>营业信息</h4>
          <el-descriptions :column="1" border size="medium">
            <el-descriptions-item label="营业时间">
              {{ merchant.businessStart || '-' }} ~ {{ merchant.businessEnd || '-' }}
            </el-descriptions-item>
            <el-descriptions-item v-if="merchant.business2Start" label="营业时间2">
              {{ merchant.business2Start }} ~ {{ merchant.business2End }}
            </el-descriptions-item>
            <el-descriptions-item label="经度/纬度">
              {{ merchant.longitude || '-' }}, {{ merchant.latitude || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="审核备注">{{ merchant.remark || '-' }}</el-descriptions-item>
          </el-descriptions>
        </el-col>
      </el-row>

      <el-divider></el-divider>

      <!-- 简介 -->
      <div v-if="merchant.summary">
        <h4>门店简介</h4>
        <p class="summary">{{ merchant.summary }}</p>
      </div>

      <!-- 资质图片 -->
      <el-divider></el-divider>
      <h4>资质文件</h4>
      <div class="license-images">
        <div class="img-item" v-if="merchant.licenseUrl">
          <p>营业执照</p>
          <el-image
              v-if="merchant.licenseUrl"
              :src="merchant.licenseUrl"
              :preview-src-list="[merchant.licenseUrl]"
              style="width: 200px; height: 140px; border-radius: 6px;"
              fit="cover"
          >
            <div slot="error" class="image-slot">
              <i class="el-icon-picture-outline"></i>
              <span>加载失败</span>
            </div>
          </el-image>
        </div>
        <div class="img-item" v-if="merchant.attachmentUrl">
          <p>承诺书</p>
          <el-image
              v-if="merchant.attachmentUrl"
              :src="merchant.attachmentUrl"
              :preview-src-list="[merchant.attachmentUrl]"
              style="width: 200px; height: 140px; border-radius: 6px;"
              fit="cover"
          >
            <div slot="error" class="image-slot">
              <i class="el-icon-picture-outline"></i>
              <span>加载失败</span>
            </div>
          </el-image>
        </div>
      </div>
    </el-card>

    <!-- 驳回弹窗 -->
    <el-dialog title="审核驳回" :visible.sync="rejectVisible" width="400px">
      <el-form>
        <el-form-item label="驳回原因" required>
          <el-input v-model="rejectReason" type="textarea" :rows="3" placeholder="请输入驳回原因"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="rejectVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="doReject">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 通过弹窗 -->
    <el-dialog title="审核通过" :visible.sync="approveVisible" width="400px">
      <el-form>
        <el-form-item label="备注">
          <el-input v-model="approveRemark" type="textarea" :rows="3" placeholder="可选填备注"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="approveVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="doApprove">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getMerchantDetail, approveMerchant, rejectMerchant } from '@/api/merchant'

export default {
  name: 'MerchantDetail',
  data() {
    return {
      merchant: {},
      loading: false,
      rejectVisible: false,
      approveVisible: false,
      rejectReason: '',
      approveRemark: '',
      submitting: false
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const id = this.$route.params.id
        this.merchant = await getMerchantDetail(id)
      } catch (e) {
        this.merchant = {}
      } finally {
        this.loading = false
      }
    },
    statusText(s) { return s === 0 ? '待审核' : s === 1 ? '已入驻' : '审核驳回' },
    handleApprove() { this.approveRemark = ''; this.approveVisible = true },
    async doApprove() {
      this.submitting = true
      try {
        await approveMerchant(this.merchant.id, this.approveRemark)
        this.$message.success('审核通过')
        this.approveVisible = false
        this.loadData()
      } catch (e) {} finally {
        this.submitting = false
      }
    },
    handleReject() { this.rejectReason = ''; this.rejectVisible = true },
    async doReject() {
      if (!this.rejectReason.trim()) return this.$message.warning('请输入驳回原因')
      this.submitting = true
      try {
        await rejectMerchant(this.merchant.id, this.rejectReason)
        this.$message.success('已驳回')
        this.rejectVisible = false
        this.loadData()
      } catch (e) {} finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.merchant-detail { padding: 0; }
.detail-header {
  display: flex;
  align-items: center;
  gap: 20px;
}
.header-info { flex: 1; }
.header-info h2 { margin: 0 0 8px; font-size: 20px; }
.header-info h2 small { font-size: 14px; color: #909399; font-weight: normal; }
.tags { display: flex; gap: 8px; }
.header-actions { display: flex; gap: 8px; }
.detail-body h4 { margin: 0 0 12px; color: #303133; font-size: 15px; }
.summary { color: #606266; line-height: 1.8; }
.license-images { display: flex; gap: 24px; flex-wrap: wrap; }
.img-item p { margin: 0 0 8px; font-size: 13px; color: #909399; }
</style>
